import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, EMPTY, of, switchMap, iif } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta, IPaginationRequest } from '@app/api/interfaces/common/pagination-meta.interface';

interface IHttpGetOptions {
  headers?: HttpHeaders;
  params?: HttpParams;
}

@Injectable({
  providedIn: 'root',
})
export class SplitRequestService {
  private baseLimit = 100;

  constructor(private httpClient: HttpClient) {}

  public get<T, IMeta extends object = object>(
    url: string,
    options: IHttpGetOptions
  ): Observable<IApiResponse<T[], IPaginationMeta & IMeta>> {
    const limit = (options.params || new HttpParams()).get('limit');

    return this.splitByLimit<T, IMeta>(
      params => {
        options.params = options.params?.set('limit', params.limit!);
        options.params = options.params?.set('offset', params.offset!);

        return this.httpClient.get<IApiResponse<T[], IPaginationMeta & IMeta>>(url, { ...options });
      },
      limit ? +limit : this.baseLimit
    );
  }

  private splitByLimit<T, IMeta extends object>(
    request: (params: Partial<IPaginationRequest>) => Observable<IApiResponse<T[], IPaginationMeta & IMeta>>,
    limit: number
  ): Observable<IApiResponse<T[], IPaginationMeta & IMeta>> {
    let finished = false;

    const params: Partial<IPaginationRequest> = {
      limit,
      offset: 0,
    };

    return request(params).pipe(
      expand((data: IApiResponse<T[], IPaginationMeta>) => {
        finished =
          !data.meta.pagination ||
          data.meta.pagination.limit + data.meta.pagination.offset >= data.meta.pagination.total;
        params.offset = params.offset! + params.limit!;

        return finished ? EMPTY : request(params);
      }),
      reduce((acc, res) => {
        Array.prototype.push.apply(acc.data, res.data);

        acc.meta.pagination.count += res.meta.pagination.count;
        acc.meta.pagination.limit += res.meta.pagination.limit;
        acc.meta.pagination.offset = 0;

        return acc;
      }),
      switchMap(data => iif(() => finished, of(data), EMPTY))
    );
  }
}
